import dayjs from "dayjs";

// Must stay in sync with api/src/helper/discount.js, which prices the actual order.

const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

export const parseDiscountDate = (value) => {
    if (!value) return null;
    const parsed = dayjs(value?.$date ?? value);
    return parsed.isValid() ? parsed : null;
};

const DAY_MS = 24 * 60 * 60 * 1000;

// Dates saved without a time (midnight UTC or local) cover the whole day; any other time is exact.
const isDateOnly = (date) => date.valueOf() % DAY_MS === 0 || date.isSame(date.startOf("day"));

export const isWithinDiscountWindow = (fromVal, toVal, now = dayjs()) => {
    const start = parseDiscountDate(fromVal);
    const end = parseDiscountDate(toVal);
    if ((fromVal && !start) || (toVal && !end)) return false;
    if (start && now.isBefore(isDateOnly(start) ? start.startOf("day") : start)) return false;
    if (end && now.isAfter(isDateOnly(end) ? end.endOf("day") : end)) return false;
    return true;
};

export const findSkuBySkuId = (productDetails, skuId) => {
    if (!productDetails || !skuId) return null;
    const id = String(skuId).trim();
    for (const variant of productDetails?.variants || []) {
        for (const size of variant?.sizes || []) {
            const sku = (size?.skus || []).find(s => String(s?.skuId || "").trim() === id);
            if (sku) return sku;
        }
    }
    return null;
};

export const getSkuPriceData = (sku) => {
    if (!sku) return {};
    const p = sku.prices?.[0] ?? sku;
    return {
        price: p?.price ?? sku?.listPrice,
        discountPrice: p?.discountPrice ?? sku?.discountPrice,
        discountPrice2: p?.discountPrice2 ?? sku?.discountPrice2,
        fromDate: p?.fromDate ?? sku?.fromDate,
        toDate: p?.toDate ?? sku?.toDate,
        fromDate2: p?.fromDate2 ?? sku?.fromDate2,
        toDate2: p?.toDate2 ?? sku?.toDate2,
    };
};

/**
 * hasDiscount: any discount is active (sale is shown struck through when hasDiscount2).
 * activePrice: what the customer pays (discountPrice2 > discountPrice > price).
 */
export const resolveDiscount = (priceData, now = dayjs()) => {
    const regular = toNumber(priceData?.price ?? priceData?.listPrice);
    const sale = toNumber(priceData?.discountPrice);
    const sale2 = toNumber(priceData?.discountPrice2);
    const fromDate2 = priceData?.fromDate2 ?? priceData?.fromDate;
    const toDate2 = priceData?.toDate2 ?? priceData?.toDate;

    const saleActive =
        sale > 0 && regular > 0 && isWithinDiscountWindow(priceData?.fromDate, priceData?.toDate, now);
    const hasDiscount2 =
        sale2 > 0 && sale > 0 && sale2 < sale && isWithinDiscountWindow(fromDate2, toDate2, now);
    const hasDiscount = saleActive || hasDiscount2;

    return {
        regular,
        sale,
        sale2,
        hasDiscount,
        hasDiscount2,
        activePrice: hasDiscount2 ? sale2 : saleActive ? sale : regular,
        percent: hasDiscount && regular > 0 && sale < regular ? Math.round(((regular - sale) / regular) * 100) : 0,
        extraPercent: hasDiscount2 ? Math.round(((sale - sale2) / sale) * 100) : 0,
    };
};

const resolveCartItem = (item, productDetails) => {
    const sku = findSkuBySkuId(productDetails, item?.item_code);
    return sku ? resolveDiscount(getSkuPriceData(sku)) : null;
};

export const getCartItemDisplayPrice = (item, productDetails) => {
    const d = resolveCartItem(item, productDetails);
    if (d?.hasDiscount) {
        return {
            regular: d.regular,
            sale: d.sale,
            sale2: d.hasDiscount2 ? d.sale2 : 0,
            hasDiscount: true,
            hasDiscount2: d.hasDiscount2,
        };
    }
    return {
        regular: toNumber(item?.regular_price),
        sale: 0,
        sale2: 0,
        hasDiscount: false,
        hasDiscount2: false,
    };
};

export const isCartItemDiscounted = (item, productDetails) => {
    const d = resolveCartItem(item, productDetails);
    if (d) return d.hasDiscount;
    return item?.sale_price > 0 && item.sale_price < item.regular_price;
};

export const repriceCartItem = (item, productDetails) => {
    const d = resolveCartItem(item, productDetails);
    return {
        regular_price: d?.regular || toNumber(item?.regular_price),
        sale_price: d?.hasDiscount ? d.activePrice : 0,
    };
};
